import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Calendar as CalendarIcon,
  MapPin,
  Loader2,
  Repeat,
  Clock,
} from "lucide-react";
import { getPublicEvents } from "../services/api";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import SiteNav from "../components/Layout/SiteNav";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

// ────────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ────────────────────────────────────────────────────────────────────────────────

// Normalize Firestore dates to JS Date
const toJsDate = (val) => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val.toDate && typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") return new Date(val);
  return new Date();
};

// Format recurrence rule for display
const formatRecurrenceRule = (recurrence) => {
  if (!recurrence || recurrence.type === "never") return null;

  const dayMap = {
    SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday',
    TH: 'Thursday', FR: 'Friday', SA: 'Saturday'
  };

  const ordinalMap = {
    1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', 5: 'Fifth',
    '-1': 'Last', '-2': 'Second to last'
  };

  const monthMap = {
    1: 'January', 2: 'February', 3: 'March', 4: 'April', 5: 'May', 6: 'June',
    7: 'July', 8: 'August', 9: 'September', 10: 'October', 11: 'November', 12: 'December'
  };

  let baseText = "";
  const interval = recurrence.interval || 1;

  if (recurrence.type === "weekly") {
    const days = recurrence.byDay && recurrence.byDay.length > 0 ? recurrence.byDay.map(d => dayMap[d] || d).join(", ") : "";
    if (interval === 1) {
      baseText = days ? `Weekly on ${days}` : "Weekly";
    } else if (interval === 2) {
      baseText = days ? `Biweekly on ${days}` : "Biweekly";
    } else {
      baseText = days ? `Every ${interval} weeks on ${days}` : `Every ${interval} weeks`;
    }
  } else if (recurrence.type === "daily") {
    baseText = interval === 1 ? "Daily" : `Every ${interval} days`;
  } else if (recurrence.type === "monthly") {
    if (recurrence.bySetPos && recurrence.bySetPos.length > 0 && recurrence.byDay && recurrence.byDay.length > 0) {
      const positions = recurrence.bySetPos.map(pos => {
        const day = dayMap[recurrence.byDay[recurrence.bySetPos.indexOf(pos)]] || recurrence.byDay[0];
        const ordinal = ordinalMap[pos] || `${pos}th`;
        return `${ordinal} ${day}`;
      }).join(" and ");
      baseText = interval === 1 ? `Monthly on the ${positions}` : `Every ${interval} months on the ${positions}`;
    } else if (recurrence.byMonthDay && recurrence.byMonthDay.length > 0) {
      const days = recurrence.byMonthDay.map(d => d < 0 ? `${Math.abs(d)} days before month end` : `day ${d}`).join(", ");
      baseText = interval === 1 ? `Monthly on ${days}` : `Every ${interval} months on ${days}`;
    } else {
      baseText = interval === 1 ? "Monthly" : `Every ${interval} months`;
    }
  } else if (recurrence.type === "yearly") {
    if (recurrence.byMonth && recurrence.byMonth.length > 0) {
      const months = recurrence.byMonth.map(m => monthMap[m] || m).join(", ");
      baseText = interval === 1 ? `Yearly in ${months}` : `Every ${interval} years in ${months}`;
    } else {
      baseText = interval === 1 ? "Yearly" : `Every ${interval} years`;
    }
  } else {
    baseText = "Recurring";
  }

  if (recurrence.count) {
    baseText += `, ${recurrence.count} times`;
  } else if (recurrence.until) {
    const untilDate = toJsDate(recurrence.until);
    baseText += ` until ${untilDate.toLocaleDateString()}`;
  }

  return baseText;
};

function EventCard({ event, onClick }) {
  const palette = useEditorialPalette();
  const eventDate = event.date?.toDate
    ? event.date.toDate()
    : new Date(event.date);
  const weekday = eventDate.toLocaleDateString("en-US", { weekday: "short" });
  const monthShort = eventDate.toLocaleDateString("en-US", { month: "short" });
  const day = eventDate.getDate();
  const year = eventDate.getFullYear();
  const formattedDate = eventDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Show recurrence badge if event is recurring
  const recurrenceText = formatRecurrenceRule(event.recurrence);

  // Function to convert URLs in text to clickable links
  const linkifyText = (text) => {
    if (!text) return null;

    // Regex to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: BRAND.gold, textDecoration: "underline" }}
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const description = event.description?.length > 200
    ? event.description.substring(0, 200) + '...'
    : event.description;

  return (
    <EditorialCard
      variant="paper"
      className="group cursor-pointer overflow-hidden p-7 sm:p-8 transition-all duration-300 hover:-translate-y-0.5"
      style={{
        boxShadow: "0 1px 0 rgba(0,0,0,0) inset",
        transition: "box-shadow 300ms ease, transform 300ms ease, border-color 300ms ease",
      }}
      onClick={onClick}
    >
      <div className="flex items-start gap-6">
        <div
          className="flex-shrink-0 flex flex-col items-center justify-center rounded-xl"
          style={{
            width: 76,
            height: 86,
            background: palette.ivory,
            border: `1px solid ${palette.hairline}`,
          }}
        >
          <div
            className="text-[10px] uppercase"
            style={{
              fontFamily: EDITORIAL_FONT.sans,
              letterSpacing: "0.16em",
              color: palette.muted,
            }}
          >
            {monthShort}
          </div>
          <div
            style={{
              fontFamily: EDITORIAL_FONT.serif,
              fontSize: 34,
              lineHeight: 1,
              fontWeight: 500,
              color: palette.ink,
              letterSpacing: "-0.02em",
              marginTop: 2,
            }}
          >
            {day}
          </div>
          <div
            className="mt-1 text-[10px] uppercase"
            style={{
              fontFamily: EDITORIAL_FONT.sans,
              letterSpacing: "0.14em",
              color: BRAND.gold,
            }}
          >
            {weekday}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {(event.masjidNickname || event.masjid) && (
            <div
              className="mb-2 text-[11px] uppercase truncate"
              style={{
                fontFamily: EDITORIAL_FONT.sans,
                letterSpacing: "0.16em",
                color: palette.muted,
                fontWeight: 600,
              }}
            >
              {event.masjidNickname || event.masjid}
            </div>
          )}

          <SerifHeading
            level={3}
            className="mb-3"
            style={{
              fontSize: 24,
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
            }}
          >
            {event.title}
          </SerifHeading>

          <div className="flex flex-col gap-2">
            <div
              className="flex items-start gap-2.5 text-sm"
              style={{ color: palette.ink2, fontFamily: EDITORIAL_FONT.sans }}
            >
              <CalendarIcon
                className="h-4 w-4 flex-shrink-0 mt-0.5"
                style={{ color: BRAND.gold }}
              />
              <span>{formattedDate}</span>
            </div>

            {(event.time || event.endTime) && (
              <div
                className="flex items-start gap-2.5 text-sm"
                style={{ color: palette.ink2, fontFamily: EDITORIAL_FONT.sans }}
              >
                <Clock
                  className="h-4 w-4 flex-shrink-0 mt-0.5"
                  style={{ color: BRAND.gold }}
                />
                <span>
                  {event.time && `${event.time}`}
                  {event.endTime && ` — ${event.endTime}`}
                </span>
              </div>
            )}

            {recurrenceText && (
              <div
                className="flex items-start gap-2.5 text-sm"
                style={{ color: palette.ink2, fontFamily: EDITORIAL_FONT.sans }}
              >
                <Repeat
                  className="h-4 w-4 flex-shrink-0 mt-0.5"
                  style={{ color: BRAND.gold }}
                />
                <span style={{ fontStyle: "italic" }}>{recurrenceText}</span>
              </div>
            )}

            {event.location && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2.5 text-sm transition-colors"
                style={{ color: palette.ink2, fontFamily: EDITORIAL_FONT.sans }}
                onClick={(e) => e.stopPropagation()}
              >
                <MapPin
                  className="h-4 w-4 flex-shrink-0 mt-0.5"
                  style={{ color: BRAND.gold }}
                />
                <span className="hover:underline">{event.location}</span>
              </a>
            )}
          </div>

          {event.description && (
            <p
              className="mt-5 text-[14px]"
              style={{
                color: palette.muted,
                fontFamily: EDITORIAL_FONT.sans,
                lineHeight: 1.6,
                borderTop: `1px solid ${palette.hairline}`,
                paddingTop: 14,
              }}
            >
              {linkifyText(description)}
            </p>
          )}
        </div>
      </div>
    </EditorialCard>
  );
}

export default function PublicEvents() {
  const navigate = useNavigate();
  const palette = useEditorialPalette();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [searching, setSearching] = useState(false); // Background refetch (search)
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null);
  const hasLoadedRef = useRef(false); // First load shows the full-page spinner

  // Request user location
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationPermission("granted");
        },
        (error) => {
          console.log("Location permission denied:", error);
          setLocationPermission("denied");
        }
      );
    } else {
      setLocationPermission("unavailable");
    }
  }, []);

  // Debounce the search box so we don't hit the API on every keystroke.
  // The query is sent to the backend, which searches across all events
  // (not just the ones already loaded near the user).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch upcoming events from API. Runs on mount, when location changes, and
  // when the (debounced) search changes. The fetch is inlined in the effect so
  // its closed-over values are captured correctly, and an AbortController
  // cancels any in-flight request when a newer one starts — otherwise a slow
  // older response could clobber a faster newer one while typing.
  useEffect(() => {
    // Wait for location permission to be determined before fetching.
    if (locationPermission === null) return;

    const controller = new AbortController();

    const fetchEvents = async () => {
      const background = hasLoadedRef.current;
      if (background) setSearching(true);
      else setLoading(true);
      setError(null);

      try {
        // With a search query, search across all events (location-independent).
        // Without one, show events near the visitor.
        const params = { signal: controller.signal };
        if (debouncedQuery) {
          params.q = debouncedQuery;
        } else if (userLocation) {
          params.lat = userLocation.latitude;
          params.lng = userLocation.longitude;
          params.radius = 50;
        }

        const response = await getPublicEvents(params);

        // Events come back already sorted and with prayer times computed
        setEvents(response.events || []);
      } catch (err) {
        // A superseded request was aborted — ignore it and let the newer one win.
        if (controller.signal.aborted || err.code === "ERR_CANCELED") return;
        console.error("Error fetching events:", err);
        setError("Failed to load events. Please try again.");
      } finally {
        // Only settle loading state if this request wasn't superseded.
        if (!controller.signal.aborted) {
          hasLoadedRef.current = true;
          setLoading(false);
          setSearching(false);
        }
      }
    };

    fetchEvents();

    return () => controller.abort();
  }, [locationPermission, userLocation, debouncedQuery]);

  // The backend already filters by the (debounced) query. We filter again on
  // the client by the live query so results respond instantly while typing,
  // before the debounced request comes back.
  const filteredEvents = events.filter((event) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      event.title?.toLowerCase().includes(query) ||
      event.description?.toLowerCase().includes(query) ||
      event.location?.toLowerCase().includes(query) ||
      event.masjid?.toLowerCase().includes(query) ||
      event.masjidNickname?.toLowerCase().includes(query)
    );
  });

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO
          ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: palette.ivory }}
      >
        <PatternTile opacity={0.04} />
        <div
          className="relative mx-auto px-4 pt-16 pb-12 sm:px-6 sm:pt-20 sm:pb-16 lg:px-10 lg:pt-24 lg:pb-20"
          style={{ maxWidth: 1320 }}
        >
          <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-16 lg:items-end">
            <div>
              <Eyebrow>Community events</Eyebrow>
              <SerifHeading
                level={1}
                className="mt-6 mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
                  lineHeight: 0.96,
                  letterSpacing: "-0.035em",
                }}
              >
                Halaqas, fundraisers,
                <br />
                Eid prayers, and the
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                  gatherings in between.
                </em>
              </SerifHeading>
              <p
                className="max-w-xl text-base sm:text-lg"
                style={{ color: palette.ink2, lineHeight: 1.55, fontFamily: EDITORIAL_FONT.sans }}
              >
                Synced directly from masajid calendars near you, with no scraping and no
                guesswork. Browse what your community is hosting this week.
              </p>
            </div>

            <div>
              <EditorialCard
                variant="paper"
                className="p-6 sm:p-7"
                style={{ borderLeft: `3px solid ${BRAND.gold}`, borderRadius: 18 }}
              >
                <div
                  className="mb-3 text-[11px] uppercase"
                  style={{
                    letterSpacing: "0.16em",
                    color: palette.muted,
                    fontFamily: EDITORIAL_FONT.sans,
                    fontWeight: 600,
                  }}
                >
                  Search
                </div>
                <div className="relative">
                  <Search
                    className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
                    style={{ color: palette.muted }}
                  />
                  <Input
                    placeholder="Title, description, location, or masjid"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 rounded-full pl-11 pr-11 text-sm"
                    style={{
                      background: palette.ivory,
                      border: `1px solid ${palette.hairline}`,
                      fontFamily: EDITORIAL_FONT.sans,
                      color: palette.ink,
                    }}
                  />
                  {searching && (
                    <Loader2
                      className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin"
                      style={{ color: BRAND.gold }}
                    />
                  )}
                </div>
              </EditorialCard>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          EVENTS LISTING
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-10 lg:py-24"
          style={{ maxWidth: 1320 }}
        >
          {error && (
            <Alert
              variant="destructive"
              className="mb-8"
              style={{
                background: "transparent",
                border: `1px solid ${BRAND.terracotta}`,
                color: BRAND.terracotta,
                borderRadius: 14,
              }}
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {(loading || (searching && events.length === 0)) && (
            <div
              className="flex flex-col items-center justify-center py-24"
              style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
            >
              <Loader2
                className="h-7 w-7 animate-spin mb-4"
                style={{ color: BRAND.gold }}
              />
              <span className="text-sm" style={{ letterSpacing: "0.04em" }}>
                {debouncedQuery
                  ? `Searching for "${debouncedQuery}"…`
                  : "Gathering events from your community…"}
              </span>
            </div>
          )}

          {!loading && events.length > 0 && (
            <div
              className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
              style={{
                borderBottom: `1px solid ${palette.hairline}`,
                paddingBottom: 24,
              }}
            >
              <div>
                <Eyebrow>What's coming up</Eyebrow>
                <SerifHeading
                  className="mt-4"
                  style={{
                    fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {searchQuery ? (
                    <>
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                        {filteredEvents.length}
                      </em>{" "}
                      matching "{searchQuery}"
                    </>
                  ) : (
                    <>
                      <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                        {filteredEvents.length}
                      </em>{" "}
                      upcoming {filteredEvents.length === 1 ? "event" : "events"}
                    </>
                  )}
                </SerifHeading>
              </div>
              <p
                className="max-w-xs text-sm"
                style={{
                  color: palette.muted,
                  fontFamily: EDITORIAL_FONT.sans,
                  lineHeight: 1.55,
                }}
              >
                Tap any event for details, directions, and reminders.
              </p>
            </div>
          )}

          {!loading && filteredEvents.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2">
              {filteredEvents.map((event) => (
                <EventCard
                  key={event.recurrenceInstanceId || event.id}
                  event={event}
                  onClick={() => navigate(`/event/${event.id}`)}
                />
              ))}
            </div>
          ) : (
            !loading &&
            !searching &&
            !error && (
              <EditorialCard
                variant="ivory"
                className="px-8 py-20 text-center sm:py-24"
                style={{ borderRadius: 22 }}
              >
                <div
                  className="mx-auto mb-8 inline-flex items-center justify-center rounded-full"
                  style={{
                    width: 84,
                    height: 84,
                    background: `${BRAND.gold}15`,
                    border: `1px solid ${palette.hairline}`,
                  }}
                >
                  <CalendarIcon className="h-9 w-9" style={{ color: BRAND.gold }} />
                </div>
                <SerifHeading
                  className="mb-4"
                  style={{
                    fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {searchQuery ? (
                    <>
                      No events <em style={{ color: BRAND.gold, fontWeight: 400 }}>found.</em>
                    </>
                  ) : (
                    <>
                      Nothing on the <em style={{ color: BRAND.gold, fontWeight: 400 }}>calendar yet.</em>
                    </>
                  )}
                </SerifHeading>
                <p
                  className="mx-auto max-w-md text-base"
                  style={{
                    color: palette.ink2,
                    fontFamily: EDITORIAL_FONT.sans,
                    lineHeight: 1.6,
                  }}
                >
                  {searchQuery
                    ? "Try a different keyword, masjid name, or location."
                    : "Check back soon. Local masajid publish gatherings every week."}
                </p>
              </EditorialCard>
            )
          )}
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA
          ══════════════════════════════════ */}
      {!loading && events.length > 0 && (
        <section
          className="relative overflow-hidden"
          style={{
            background: palette.ivory,
            borderTop: `1px solid ${palette.hairline}`,
          }}
        >
          <div
            className="relative mx-auto grid gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-10"
            style={{ maxWidth: 1320 }}
          >
            <div>
              <Eyebrow>For masajid</Eyebrow>
              <SerifHeading
                className="mt-5 mb-7"
                style={{
                  fontSize: "clamp(2rem, 5vw, 4rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                }}
              >
                Want to post
                <br />
                an event?{" "}
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                  Get started.
                </em>
              </SerifHeading>
              <p
                className="mb-8 max-w-xl text-base sm:text-lg"
                style={{
                  color: palette.ink2,
                  fontFamily: EDITORIAL_FONT.sans,
                  lineHeight: 1.55,
                }}
              >
                Register as a masjid admin to share halaqas, fundraisers, and
                community gatherings with everyone nearby, free forever.
              </p>
              <PillButton variant="primary" onClick={() => navigate("/signup")}>
                Get started
                <span aria-hidden>→</span>
              </PillButton>
            </div>

            <EditorialCard
              variant="paper"
              className="p-8 sm:p-10"
              style={{ borderLeft: `3px solid ${BRAND.gold}`, borderRadius: 20 }}
            >
              <div
                className="mb-4 text-xs uppercase"
                style={{
                  letterSpacing: "0.14em",
                  color: palette.muted,
                  fontFamily: EDITORIAL_FONT.sans,
                  fontWeight: 600,
                }}
              >
                Why masajid use Muslim+
              </div>
              <ul
                className="flex flex-col gap-4 text-[15px]"
                style={{
                  color: palette.ink2,
                  fontFamily: EDITORIAL_FONT.sans,
                  lineHeight: 1.55,
                }}
              >
                <li className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-2 flex-shrink-0"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: BRAND.gold,
                    }}
                  />
                  <span>
                    <strong style={{ color: palette.ink, fontWeight: 600 }}>
                      Reach the ummah locally.
                    </strong>{" "}
                    Events surface to Muslims within 50 miles of your masjid.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-2 flex-shrink-0"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: BRAND.gold,
                    }}
                  />
                  <span>
                    <strong style={{ color: palette.ink, fontWeight: 600 }}>
                      Recurring events made easy.
                    </strong>{" "}
                    Weekly halaqas, monthly fundraisers. Set it once.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-2 flex-shrink-0"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: BRAND.gold,
                    }}
                  />
                  <span>
                    <strong style={{ color: palette.ink, fontWeight: 600 }}>
                      No ads, no fees.
                    </strong>{" "}
                    A free, ad-free home for your community calendar.
                  </span>
                </li>
              </ul>
            </EditorialCard>
          </div>
        </section>
      )}
    </EditorialShell>
  );
}
